import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getRouteMutationKey } from '.';
import { ReportCreatedProps, reportService } from '../../../../application/ReportService';
import { ReportStatus } from '../../../../constants/Stops';
import { CreateReportDataProps, ImageUploadedProps } from '../../../../domain/report/StopReportCreate';
import { Route } from '../../../../domain/route/Route';
import { Stop } from '../../../../domain/stop/Stop';
import { useCaptureEvents } from '../../../../hooks/useCaptureEvents';
import { StopReportCreateMapper } from '../../../../mappers/report/StopReportCreateMapper';
import { useLocationContext } from '../../context/locationContext/context';
import { usePendingReports } from '../../hooks/pendingReports/usePendingReports';
import { useRetrieveVehicle } from '../../hooks/useRetrieveVehicle';
import { RouteActionType } from '../context/actions';
import { useRouteContext } from '../context/context';
import { useStartStop } from './useStartStop';

interface UseCreateStopReportProps {
  status: ReportStatus;
  stop: Stop;
  images?: Array<ImageUploadedProps>;
  reportData?: CreateReportDataProps;
}

type UseCreateStopReportResponseProps = ServiceResponse<
  ({ status, stop, images, reportData }: UseCreateStopReportProps) => void,
  void
>;

// Hooks act as 'Adapter' layer.
export const useCreateStopReport = (): UseCreateStopReportResponseProps => {
  const { data: vehicleData } = useRetrieveVehicle();
  const { locationState } = useLocationContext();
  const { routeState, routeDispatch } = useRouteContext();
  const { refetch } = usePendingReports();
  const queryClient = useQueryClient();
  const { captureCreateReport } = useCaptureEvents();

  const { timeInStop: counter, clearReportDate } = useStartStop();

  const { isLoading, isSuccess, isError, mutate } = useMutation<ReportCreatedProps, unknown, UseCreateStopReportProps>(
    ({ status, stop, images, reportData }: UseCreateStopReportProps): Promise<ReportCreatedProps> => {
      if (!routeState.route?.project_id) {
        throw new Error(`routeState.route?.project_id does not exist`);
      }

      const stopReport = StopReportCreateMapper.toDomain({
        status,
        stopId: stop.id,
        projectFields: vehicleData!.project.fields,
        ...(locationState.currentPosition
          ? { currentPosition: { lat: locationState.currentPosition.lat, lng: locationState.currentPosition.lng } }
          : {}),
        timeInStop: counter,
        images,
        reportData,
      });

      const reportCreated = reportService.createReport(stopReport).then((reportCreated) => {
        if (reportCreated.queued) {
          console.info(`Report for stop ${stop.id} queued successfully.`);
          refetch();
          console.info(`Refetch:`);
        }

        return reportCreated;
      });
      return reportCreated;
    },
    {
      onSuccess: ({ queued, stopReport, stopStatus }, { stop, reportData }) => {
        const routeMutationKey = getRouteMutationKey({ routeId: stop.route_id });

        if (queued) {
          const previousQueryContent = queryClient.getQueryData<Route>(routeMutationKey);
          if (!previousQueryContent) {
            throw new Error(`previousQueryContent does not exist`);
          }

          previousQueryContent.stops = previousQueryContent.getSortedStopList({}).map((s) => {
            if (s.id === stop.id) {
              return Stop.create(
                {
                  ...s.props,
                  stop_fields: structuredClone(s.stop_fields),
                  status: stopStatus,
                  report: stopReport,
                },
                s.id
              );
            }
            return s;
          });

          queryClient.setQueryData<Route>(routeMutationKey, previousQueryContent);
          routeDispatch({ type: RouteActionType.FETCH_ROUTE_SUCCESS, payload: previousQueryContent });
        } else {
          captureCreateReport({
            stopReport,
            status: stopStatus,
            isOffline: false,
            hasLocation: locationState.currentPosition ? true : false,
            hasCreatedTasks: (reportData?.temporalReportTasks?.length ?? 0) > 0 ? true : false,
          });
          queryClient.invalidateQueries(routeMutationKey);
        }

        clearReportDate();
      },
      onError: () => {},
    }
  );

  const createReport = ({ status, stop, images, reportData }: UseCreateStopReportProps) => {
    mutate({
      status,
      stop,
      images,
      reportData,
    });
  };

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: createReport,
  };
};
